import type { OverrideOptionInput } from '@erp/rpc';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

/** A warehouse holding the SKU, as the fulfillment-candidates lookup lists it. */
type TransferSource = {
  warehouseId: string;
  warehouseName: string;
  warehouseCode: string | null;
  divisionName: string | null;
  divisionCode: string | null;
  onHandQuantity: number;
};

/** The Source column's value for "buy from the cart's vendor". */
export const VENDOR_SOURCE = 'vendor';

/**
 * Where the line ships from: the vendor, or the warehouse a pure transfer
 * pulls from (GEN-3023). A buy-and-transfer pivot still buys from the vendor.
 */
export function currentSource(line: Pick<AssignmentLine, 'topOption'>): string {
  const option = line.topOption;
  if (option?.action === 'transfer' && option.sourceWarehouseId != null) {
    return option.sourceWarehouseId;
  }
  return VENDOR_SOURCE;
}

export interface SourceChoice {
  warehouseId: string;
  warehouseName: string;
  /** The AS400 location code (e.g. `RDDCO`); null when not synced. */
  warehouseCode: string | null;
  divisionName: string | null;
  /** The AS400 division code (e.g. `RA`), which tells the Londons apart. */
  divisionCode: string | null;
  /** Null for the line's current source when the lookup has not loaded it. */
  onHandQuantity: number | null;
}

/**
 * The warehouses the buyer can pick, with the line's current source kept
 * first even when it holds no stock now, so the picker can always name it.
 */
export function sourceChoices(
  line: AssignmentLine,
  transferSources: ReadonlyArray<TransferSource> | undefined,
): SourceChoice[] {
  const choices: SourceChoice[] = (transferSources ?? []).map((source) => ({
    warehouseId: source.warehouseId,
    warehouseName: source.warehouseName,
    warehouseCode: source.warehouseCode,
    divisionName: source.divisionName,
    divisionCode: source.divisionCode,
    onHandQuantity: source.onHandQuantity,
  }));
  const option = line.topOption;
  const current = option?.sourceWarehouseId;
  if (
    option?.action === 'transfer' &&
    current != null &&
    !choices.some((choice) => choice.warehouseId === current)
  ) {
    choices.unshift({
      warehouseId: current,
      warehouseName: option.sourceWarehouseName ?? current,
      warehouseCode: null,
      divisionName: null,
      divisionCode: null,
      onHandQuantity: null,
    });
  }
  return choices;
}

/**
 * Whether a typed search keeps a choice in the picker (GEN-3148): every
 * whitespace-separated term must appear in the location code, its name, or
 * the division's code or name, so "lon ra" finds the London in division RA.
 */
export function matchesSourceSearch(
  choice: Pick<
    SourceChoice,
    'warehouseCode' | 'warehouseName' | 'divisionCode' | 'divisionName'
  >,
  search: string,
): boolean {
  // A `*` is a wildcard by habit (Robert, GEN-3147: "a QE wildcard"); every
  // term already matches anywhere, so it is dropped.
  const terms = search
    .toLowerCase()
    .replaceAll('*', ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = [
    choice.warehouseCode,
    choice.warehouseName,
    choice.divisionCode,
    choice.divisionName,
  ]
    .filter((part): part is string => part != null)
    .join(' ')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

type SourceOverrideResult =
  | { kind: 'save'; input: OverrideOptionInput }
  | { kind: 'noop' }
  | { kind: 'missing-context' };

/** The quantity the line carries now, whichever leg holds it. */
function lineQuantity(line: AssignmentLine): number {
  const option = line.topOption;
  return option?.buyQuantity ?? option?.transferQuantity ?? line.neededQuantity;
}

/**
 * The override that moves a line to `next` (a warehouse id or
 * {@link VENDOR_SOURCE}) at the quantity it carries now. The line stays in its
 * vendor cart either way; a transfer is released as a transfer order with the
 * cart. Going back to the vendor buys at the vendor's current price, which the
 * server reprices at the quantity's tier.
 */
export function buildSourceOverride({
  line,
  next,
  supplierId,
  vendorUnitPrice,
}: {
  line: AssignmentLine;
  next: string;
  /** The cart's vendor. */
  supplierId: string | undefined;
  /** The vendor's unit price from the candidates lookup, when loaded. */
  vendorUnitPrice: number | null | undefined;
}): SourceOverrideResult {
  const option = line.topOption;
  if (line.status !== 'pending') return { kind: 'noop' };
  if (
    option == null ||
    option.action === 'stock' ||
    option.planKind !== 'direct'
  ) {
    return { kind: 'noop' };
  }
  if (next === currentSource(line)) return { kind: 'noop' };
  const quantity = lineQuantity(line);
  if (!(quantity > 0)) return { kind: 'missing-context' };

  if (next !== VENDOR_SOURCE) {
    return {
      kind: 'save',
      input: {
        suggestionId: line.suggestionId,
        action: 'transfer',
        supplierId: null,
        shipToWarehouseId: null,
        buyQuantity: null,
        unitCost: null,
        sourceWarehouseId: next,
        transferQuantity: quantity,
        setChosen: true,
      },
    };
  }
  if (supplierId == null || vendorUnitPrice == null) {
    return { kind: 'missing-context' };
  }
  return {
    kind: 'save',
    input: {
      suggestionId: line.suggestionId,
      action: 'buy',
      supplierId,
      shipToWarehouseId: line.shipToWarehouseId,
      buyQuantity: quantity,
      unitCost: vendorUnitPrice,
      sourceWarehouseId: null,
      transferQuantity: null,
      setChosen: true,
    },
  };
}
